import { useCallback, useEffect, useState } from "react";
import type { MediaItem, Playlist } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { CatalogBrowser } from "./CatalogBrowser";
import { ClipLightboxController } from "./ClipLightboxController";

/**
 * Content-first curation. Browse the catalog, then for any clip open a picker to
 * toggle it across many collections at once or spin up a new one from it — so
 * collections behave like keywords you apply to content.
 */
export function ContentReview() {
  const [collections, setCollections] = useState<Playlist[]>([]);
  // clipKey -> collection ids that contain it.
  const [memberships, setMemberships] = useState<Record<string, string[]>>({});
  const [items, setItems] = useState<MediaItem[]>([]);
  const [active, setActive] = useState<MediaItem | null>(null);

  const loadCollections = useCallback(async () => {
    const { collections: cs } = await adminApi.collections.list();
    setCollections(cs);
  }, []);

  useEffect(() => {
    void loadCollections();
  }, [loadCollections]);

  // Whenever the browser's visible clips change, remember them + look up their
  // collection membership (drives the "In N" badges and the tag pills).
  const onItems = useCallback(async (list: MediaItem[]) => {
    setItems(list);
    if (list.length === 0) return;
    const keys = list.map(clipKey);
    const res = await adminApi.clipCollections(keys).catch(() => null);
    const found: Record<string, string[]> = res?.memberships ?? {};
    setMemberships((prev) => {
      const next = { ...prev };
      for (const k of keys) next[k] = found[k] ?? next[k] ?? [];
      return next;
    });
  }, []);

  function idsFor(clip: MediaItem): string[] {
    return memberships[clipKey(clip)] ?? [];
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-100">Review</h1>
        <p className="text-xs text-zinc-500">
          Browse the catalog, then tag each clip into any collections at once — or create a
          new one from it. Collections work like keywords here.
        </p>
      </header>

      <CatalogBrowser
        title="Content"
        onItemsChange={onItems}
        onClipClick={(clip) => setActive(clip)}
        renderTopRight={(clip) => <Tags names={idsFor(clip).map((id) => nameOf(collections, id))} />}
        renderAction={(clip) => (
          <button
            type="button"
            onClick={() => setActive(clip)}
            className="ml-auto rounded bg-[var(--color-accent)]/90 px-2 py-1 text-[10px] font-medium text-white hover:bg-[var(--color-accent)]"
          >
            {idsFor(clip).length > 0 ? `In ${idsFor(clip).length} ✎` : "Open ✎"}
          </button>
        )}
      />

      {active ? (
        <ClipLightboxController
          key={clipKey(active)}
          list={items}
          startKey={clipKey(active)}
          onClose={() => setActive(null)}
          onChanged={() => {
            void loadCollections();
            void onItems(items);
          }}
        />
      ) : null}
    </div>
  );
}

function nameOf(collections: Playlist[], id: string): string {
  return collections.find((c) => c.id === id)?.name ?? "—";
}

/** Compact keyword-style tag pill(s) shown on a clip tile. */
function Tags({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  const label = names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
  return (
    <span className="max-w-[8rem] truncate rounded bg-[var(--color-accent)]/85 px-1.5 py-0.5 text-[9px] font-medium text-white">
      {label}
    </span>
  );
}
